import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  EWFParamType,
  ISymbol,
  IWFParameter
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import type { ChangeEvent } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { WithForm } from "../../../../shared/hoc/with-form";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { SymbolPicker } from "../../right-panel/properties-panel/symbol-picker";
import { WORKFLOW_SCALAR_TYPES } from "./workflow-parameter-type-options";

export interface IWorkflowParameterItemState {
  /** REMOVE is running. */
  isRemoving: boolean;
  /** Where UPDATE stands. Owned by `SaveStatus`, never set directly. */
  updateStatus: KuikaButtonStatus;
  tempParam: IWFParameter | undefined;
  noUpdate: boolean;
}

export interface IWorkflowParameterItemProps {
  property: IWFParameter;
  /** Resolves `true` once the edit has been saved, `false` when a check refused it. */
  onParamUpdated: (orgParamId: string, newParam: IWFParameter) => Promise<boolean>;
  onParamDeleted: (orgParamId: string) => Promise<void>;
}

class WorkflowParameterItem extends PureSdComponent<
  IWorkflowParameterItemProps & IWithSDProps & IWithContextProp,
  IWorkflowParameterItemState
> {
  /**
   * UPDATE's state machine. The card stays open after an edit, so the button is where it is
   * confirmed; `canStart` keeps a second UPDATE out while the ✓ is up.
   */
  private readonly updateStatus: SaveStatus = new SaveStatus((updateStatus: KuikaButtonStatus): void => {
    this.setState({ updateStatus });
  });

  constructor(props: IWorkflowParameterItemProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
    this.state = {
      isRemoving: false,
      updateStatus: "idle",
      tempParam: undefined,
      noUpdate: true
    };
  }

  handleNameChange = (e) => {
    const { value } = e.target;
    const tempParam = _.clone(this.state.tempParam);
    if (tempParam !== undefined) {
      tempParam!.name = value;
      this.setState({ tempParam, noUpdate: _.isEqual(tempParam, this.props.property) });
    }
  };

  handleTypeChange = (value) => {
    const tempParam = _.clone(this.state.tempParam);
    if (tempParam !== undefined) {
      tempParam.dataType = value as BackEndValueType;
      this.setState({ tempParam, noUpdate: _.isEqual(tempParam, this.props.property) });
    }
  };

  handleParamTypeChange = (value, paramType: EWFParamType) => {
    const tempParam = _.clone(this.state.tempParam);
    if (tempParam !== undefined) {
      switch (paramType) {
        case EWFParamType.Input:
          tempParam.isInput = value;
          tempParam.parameterType = value ? paramType : EWFParamType.Bag;
          break;
        case EWFParamType.Output:
          tempParam.isOutput = value;
          break;
      }
      this.setState({ tempParam, noUpdate: _.isEqual(tempParam, this.props.property) });
    }
  };

  handleParameterDefaultValue = (value) => {
    const tempParam = _.clone(this.state.tempParam);
    if (tempParam !== undefined) {
      tempParam.defaultValue = value as ISymbol;
      this.setState({ tempParam, noUpdate: _.isEqual(tempParam, this.props.property) });
    }
  };

  removeParam = async (wfParamId: string): Promise<void> => {
    this.setState({ isRemoving: true });
    await this.props.onParamDeleted(wfParamId);
    this.setState({ isRemoving: false, noUpdate: true, tempParam: _.cloneDeep(this.props.property) });
  };

  updateParam = async (wfParamId: string, tempParam: IWFParameter): Promise<void> => {
    if (!this.updateStatus.canStart) return;
    this.updateStatus.start();
    try {
      const saved: boolean = await this.props.onParamUpdated(wfParamId, tempParam);
      if (saved) {
        this.updateStatus.succeed();
      } else {
        // A check refused the edit and its warning said why.
        this.updateStatus.reset();
      }
    } catch (error: unknown) {
      this.updateStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
    this.setState({ noUpdate: true, tempParam: _.cloneDeep(this.props.property) });
  };

  componentWillUnmount(): void {
    this.updateStatus.dispose();
  }

  componentDidMount() {
    this.setState({
      tempParam: _.cloneDeep(this.props.property),
      noUpdate: true
    });
  }

  // componentDidUpdate(prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & IWorkflowParameterItemProps & IWithContextProp>, prevState: Readonly<IPureDesignComponentState & IWorkflowParameterItemState>, snapshot?: any): void {
  //   if(!_.isEqual(this.state.tempParam, this.props.property)){
  //     this.setState(this.)
  //   }
  // }

  render() {
    const { tempParam, noUpdate } = this.state;
    if (tempParam)
      return (
        <div id={`workflow-param-${tempParam.id}`}>
          <span id={`param-name-label-${tempParam.id}`} className="parameters-fields__name">
            Name
          </span>
          <KuikaInput
            data-testid={`workflowParamNameEditInput-${this.props.property.name?.replace(/\s+/g, "").toLowerCase()}`}
            id={`param-name-input-${tempParam.id}`}
            className="parameters-fields__input"
            value={tempParam.name}
            placeholder="Parameter"
            onInput={this.handleNameChange.bind(this)}
          />
          <span id={`param-type-label-${tempParam.id}`} className="parameters-fields__name">
            Type
          </span>
          <KuikaSelectbox
            data-testid={`workflowParamTypeEditDropdown-${this.props.property.name?.replace(/\s+/g, "").toLowerCase()}`}
            id={`param-type-select-${tempParam.id}`}
            searchable
            style={{ width: "100%", marginBottom: 8 }}
            className="parameters-fields__input"
            onChange={this.handleTypeChange.bind(this)}
            placeholder="Type"
            value={tempParam.dataType}
            options={WORKFLOW_SCALAR_TYPES.map((item: string): KuikaSelectboxOption => ({ value: item, label: item }))}
          />
          {tempParam.isBusinessObject && (
            <>
              <div style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
                <div
                  id={`param-input-checkbox-container-${tempParam.id}`}
                  style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
                >
                  <span id={`param-input-label-${tempParam.id}`} className="parameters-fields__name">
                    Process Input Parameter
                  </span>
                  {/* The visible name is the sibling span above, so the accessible name is
                      supplied here rather than through `label` — moving the text into the
                      checkbox would collapse this row's space-between layout. */}
                  <KuikaCheckbox
                    dataTestId={`workflowParamInputCheckbox-${this.props.property.name
                      ?.replace(/\s+/g, "")
                      .toLowerCase()}`}
                    ariaLabel="Process Input Parameter"
                    checked={tempParam.isInput === true}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      this.handleParamTypeChange(e.target.checked, EWFParamType.Input)
                    }
                  />
                </div>
                <div
                  id={`param-output-checkbox-container-${tempParam.id}`}
                  style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
                >
                  <span id={`param-output-label-${tempParam.id}`} className="parameters-fields__name">
                    Process Output Parameter
                  </span>
                  <KuikaCheckbox
                    dataTestId={`workflowParamOutputCheckbox-${this.props.property.name
                      ?.replace(/\s+/g, "")
                      .toLowerCase()}`}
                    ariaLabel="Process Output Parameter"
                    checked={tempParam.isOutput === true}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      this.handleParamTypeChange(e.target.checked, EWFParamType.Output)
                    }
                  />
                </div>
              </div>
            </>
          )}
          <span
            id={`param-default-label-${tempParam.id}`}
            className="parameters-fields__name"
            style={{ display: "flex", paddingBottom: 4, paddingTop: 4 }}
          >
            Default Value
          </span>
          <SymbolPicker
            key={`param-symbol-picker-${tempParam.id}`}
            symbol={tempParam.defaultValue}
            changeType="onKeyPress"
            valueType={tempParam.dataType}
            hideWFParams={true}
            targetCardinality={ECardinality.One}
            hideCategoryChange={true}
            onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason | undefined) => {
              this.handleParameterDefaultValue(symbol);
            }}
          />
          <div id={`param-buttons-container-${tempParam.id}`} className="parameters-fields__buttons">
            <KuikaButton
              data-testid={`workflowParamRemoveButton-${this.props.property.name?.replace(/\s+/g, "").toLowerCase()}`}
              id={`param-remove-button-${tempParam.id}`}
              className="parameters-fields__remove-button"
              type="text"
              loading={this.state.isRemoving}
              disabled={this.state.updateStatus === "loading"}
              onClick={() => void this.removeParam(this.props.property.id)}
            >
              REMOVE
            </KuikaButton>
            <KuikaButton
              data-testid={`workflowParamUpdateButton-${this.props.property.name?.replace(/\s+/g, "").toLowerCase()}`}
              id={`param-update-button-${tempParam.id}`}
              disabled={noUpdate || this.state.isRemoving}
              type="text"
              status={this.state.updateStatus}
              onClick={() => void this.updateParam(this.props.property.id, tempParam)}
            >
              UPDATE
            </KuikaButton>
          </div>
        </div>
      );
    return <></>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const workflowParameterItem = withContext<IWorkflowParameterItemProps>(
  WithForm<IWorkflowParameterItemProps>(withSD<IWorkflowParameterItemProps>(WorkflowParameterItem))
);
export { workflowParameterItem as WorkflowParameterItem };
